import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {
    Box,
    Button,
    ButtonGroup,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    MenuItem,
    Typography,
    useTheme
} from '@mui/material';
import { usePostHog } from '@posthog/react';
import { TrackedTextField } from 'src/theme/StyledFields';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import { capture } from '../../analytics/posthog';
import { useAppContext } from '../../context/AppContext';
import { LessonPlanRecordingProvider, useLessonPlanRecording } from '../../context/LessonPlanRecordingContext';
import { LessonPlanModel, LessonPlanSessionModel, SessionSlideModel } from '../../object-actions/types/types';
import ProgressSurveyCta from '../ProgressSurveyCta';
import LessonPlanCard from './LessonPlanCard';
import { areAllSlidesTrackedForSession, getFirstIncompleteSessionIndex, getFirstUnstartedSlideIndex } from './lessonPlanProgressUtils';

interface LessonPlanRendererProps {
    entity: LessonPlanModel;
}

const LessonPlanRenderer: React.FC<LessonPlanRendererProps> = ({ entity }) => {
    if (!entity?.sessions || entity.sessions.length === 0) {
        return (
            <Box>
                <Typography variant="body1" color="text.secondary">
                    No lesson plan content available.
                </Typography>
            </Box>
        );
    }

    return (
        <LessonPlanRecordingProvider>
            <LessonPlanRendererContent entity={entity} />
        </LessonPlanRecordingProvider>
    );
};

interface LessonPlanRendererContentProps {
    entity: LessonPlanModel;
}

const LessonPlanRendererContent: React.FC<LessonPlanRendererContentProps> = ({ entity }) => {
    const theme = useTheme();
    const posthog = usePostHog();
    const { organization } = useAppContext();
    const {
        activeSessionId,
        setActiveSessionId,
        setActiveSlideId,
        stopRecording,
        isStarted,
        isRecording,
        uploadQueue,
        uploading,
        getCurrentRecording,
        waitForUploadQueueIdle,
        abandonRecordingAndUploads
    } = useLessonPlanRecording();
    const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [showChangeLessonDialog, setShowChangeLessonDialog] = useState(false);
    const [pendingSessionId, setPendingSessionId] = useState<number | null>(null);
    const [navLeaveBusy, setNavLeaveBusy] = useState(false);
    const progressUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const recordingGuardRef = useRef({
        isRecording,
        uploadQueueLength: uploadQueue.length,
        uploading
    });
    recordingGuardRef.current = {
        isRecording,
        uploadQueueLength: uploadQueue.length,
        uploading
    };

    const shouldBlockRouteChange = useCallback(
        () =>
            recordingGuardRef.current.isRecording() ||
            recordingGuardRef.current.uploadQueueLength > 0 ||
            recordingGuardRef.current.uploading,
        [isRecording, uploadQueue.length, uploading]
    );

    const blocker = useBlocker(({ currentLocation, nextLocation }) => {
        if (currentLocation.pathname === nextLocation.pathname) {
            return false;
        }
        return shouldBlockRouteChange();
    });

    useEffect(() => {
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            const g = recordingGuardRef.current;
            if (g.isRecording() || g.uploadQueueLength > 0 || g.uploading) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, []);

    const handleNavigationStopAndUpload = async () => {
        if (blocker.state !== 'blocked') return;
        setNavLeaveBusy(true);
        try {
            if (getCurrentRecording()) {
                await stopRecording(true, false);
            }
            await waitForUploadQueueIdle();
            blocker.proceed();
        } catch (err) {
            console.error('Stop and upload before navigation failed:', err);
        } finally {
            setNavLeaveBusy(false);
        }
    };

    const handleNavigationLeaveAnyway = async () => {
        if (blocker.state !== 'blocked') return;
        setNavLeaveBusy(true);
        try {
            await abandonRecordingAndUploads();
            blocker.proceed();
        } catch (err) {
            console.error('Abandon recording before navigation failed:', err);
        } finally {
            setNavLeaveBusy(false);
        }
    };

    const handleNavigationStay = () => {
        if (blocker.state === 'blocked') {
            blocker.reset();
        }
    };

    const sessions = entity.sessions || [];
    const myRoles = entity.my_roles;

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (progressUpdateTimeoutRef.current) {
                clearTimeout(progressUpdateTimeoutRef.current);
            }
        };
    }, []);

    // When the lesson plan is loaded or reloaded, open the first incomplete session and scroll to it
    useEffect(() => {
        if (!sessions.length) return;
        const idx = getFirstIncompleteSessionIndex(sessions);
        const ses = sessions[idx];
        const j = getFirstUnstartedSlideIndex(ses) ?? 0;
        const sl = (ses?.slides || []).filter((s): s is SessionSlideModel => s != null)[j] ?? (ses?.slides || [])[0];
        const lpId = entity?.id;
        if (ses?.id != null && posthog && lpId) {
            capture(posthog, "lesson_session_viewed", {
                lesson_plan_id: lpId,
                session_id: ses.id,
                session_number: ses.session_number ?? ses.id,
                organization_id: organization?.id,
                source: 'accordion'
            });
        }
        setActiveSessionId(ses?.id ?? null);
        setActiveSlideId(sl?.id ?? null);
        setTimeout(() => {
            if (ses?.id != null) scrollToCardById(ses.id);
        }, 0);
    }, [entity, setActiveSessionId, setActiveSlideId]);

    const scrollToCardById = (sessionId: number) => {
        const idx = sessions.findIndex((s) => s.id === sessionId);
        if (idx < 0) return;
        const cardElement = cardRefs.current[idx];
        if (cardElement) {
            const elementTop = cardElement.getBoundingClientRect().top;
            const offset = 20;
            const scrollPosition = window.scrollY + elementTop - offset;
            window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
        }
    };

    const handlePlanChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const sessionId = Number(event.target.value);
        if (Number.isNaN(sessionId)) return;
        if (isStarted && activeSessionId != null && sessionId !== activeSessionId) {
            setPendingSessionId(sessionId);
            setShowChangeLessonDialog(true);
            return;
        }
        const ses = sessions.find((s) => s.id === sessionId);
        if (!ses) return;
        fireSessionViewed(ses, 'dropdown');
        setActiveSessionId(ses.id);
        const j = getFirstUnstartedSlideIndex(ses) ?? 0;
        const sl = (ses.slides || []).filter((s): s is SessionSlideModel => s != null)[j] ?? (ses.slides || [])[0];
        setActiveSlideId(sl?.id ?? null);
        setTimeout(() => scrollToCardById(sessionId), 0);
    };

    const handleAccordionChange = (session: LessonPlanSessionModel) => (
        _event: React.SyntheticEvent<Element, Event>,
        expanded: boolean
    ) => {
        if (expanded) {
            if (isStarted && activeSessionId != null && session.id !== activeSessionId) {
                setPendingSessionId(session.id);
                setShowChangeLessonDialog(true);
                return;
            }
            fireSessionViewed(session, 'accordion');
            setActiveSessionId(session.id);
            const j = getFirstUnstartedSlideIndex(session) ?? 0;
            const sl = (session.slides || []).filter((s): s is SessionSlideModel => s != null)[j] ?? (session.slides || [])[0];
            setActiveSlideId(sl?.id ?? null);
            setTimeout(() => scrollToCardById(session.id), 0);
        } else {
            setActiveSessionId(null);
        }
    };

    const handleNext = () => {
        const idx = activeSessionId != null ? sessions.findIndex((s) => s.id === activeSessionId) : -1;
        const newIdx = idx < 0 ? 0 : Math.min(idx + 1, sessions.length - 1);
        const nextSession = sessions[newIdx];
        if (!nextSession) return;
        if (isStarted && activeSessionId != null && nextSession.id !== activeSessionId) {
            setPendingSessionId(nextSession.id);
            setShowChangeLessonDialog(true);
            return;
        }
        fireSessionViewed(nextSession, 'prev_next');
        setActiveSessionId(nextSession.id);
        const j = getFirstUnstartedSlideIndex(nextSession) ?? 0;
        const sl = (nextSession.slides || []).filter((s): s is SessionSlideModel => s != null)[j] ?? (nextSession.slides || [])[0];
        setActiveSlideId(sl?.id ?? null);
        setTimeout(() => scrollToCardById(nextSession.id), 0);
    };

    const handlePrevious = () => {
        const idx = activeSessionId != null ? sessions.findIndex((s) => s.id === activeSessionId) : -1;
        const newIdx = idx < 0 ? 0 : Math.max(idx - 1, 0);
        const prevSession = sessions[newIdx];
        if (!prevSession) return;
        if (isStarted && activeSessionId != null && prevSession.id !== activeSessionId) {
            setPendingSessionId(prevSession.id);
            setShowChangeLessonDialog(true);
            return;
        }
        fireSessionViewed(prevSession, 'prev_next');
        setActiveSessionId(prevSession.id);
        const j = getFirstUnstartedSlideIndex(prevSession) ?? 0;
        const sl = (prevSession.slides || []).filter((s): s is SessionSlideModel => s != null)[j] ?? (prevSession.slides || [])[0];
        setActiveSlideId(sl?.id ?? null);
        setTimeout(() => scrollToCardById(prevSession.id), 0);
    };

    const handleDialogStopAndUpload = async () => {
        setShowChangeLessonDialog(false);
        await stopRecording(true, false).catch((e) => console.error('Stop & upload:', e));
        if (pendingSessionId != null) {
            const ses = sessions.find((s) => s.id === pendingSessionId);
            if (ses) {
                fireSessionViewed(ses, 'accordion');
                const j = getFirstUnstartedSlideIndex(ses) ?? 0;
                const sl = (ses.slides || []).filter((s): s is SessionSlideModel => s != null)[j] ?? (ses.slides || [])[0];
                setActiveSessionId(ses.id);
                setActiveSlideId(sl?.id ?? null);
                setTimeout(() => scrollToCardById(pendingSessionId), 0);
            }
        }
        setPendingSessionId(null);
    };

    const handleDialogDiscard = async () => {
        setShowChangeLessonDialog(false);
        await stopRecording(true, true).catch((e) => console.error('Discard:', e));
        if (pendingSessionId != null) {
            const ses = sessions.find((s) => s.id === pendingSessionId);
            if (ses) {
                fireSessionViewed(ses, 'accordion');
                const j = getFirstUnstartedSlideIndex(ses) ?? 0;
                const sl = (ses.slides || []).filter((s): s is SessionSlideModel => s != null)[j] ?? (ses.slides || [])[0];
                setActiveSessionId(ses.id);
                setActiveSlideId(sl?.id ?? null);
                setTimeout(() => scrollToCardById(pendingSessionId), 0);
            }
        }
        setPendingSessionId(null);
    };

    const handleDialogCancel = () => {
        setShowChangeLessonDialog(false);
        setPendingSessionId(null);
    };

    const lessonPlanId: number | null = entity?.id
        ? (typeof entity.id === 'number' ? entity.id : Number(entity.id))
        : null;

    const fireSessionViewed = (session: LessonPlanSessionModel, source: 'accordion' | 'dropdown' | 'prev_next') => {
        if (!posthog || !session?.id || !lessonPlanId) return;
        capture(posthog, "lesson_session_viewed", {
            lesson_plan_id: lessonPlanId,
            session_id: session.id,
            session_number: session.session_number ?? session.id,
            organization_id: organization?.id,
            source
        });
    };

    const activeSession = sessions.find((s) => s.id === activeSessionId) ?? sessions[0];
    const sessionSlides = (activeSession?.slides || []).filter((s: SessionSlideModel | null | undefined): s is SessionSlideModel => s != null);
    const isFirstSession = sessions[0]?.id === activeSessionId;
    const isLastSession = sessions[sessions.length - 1]?.id === activeSessionId;

    return (
        <Box data-testid="lesson-plan-renderer">
            {sessions.length > 1 && (
                <Box sx={{ mb: 2, p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
                        <TrackedTextField
                            select
                            label="Lesson Plans"
                            variant="standard"
                            size="small"
                            sx={{ flexGrow: 1 }}
                            value={activeSessionId ?? ''}
                            onChange={handlePlanChange}
                            data-attr="lesson-plan-renderer_session-select"
                        >
                            {sessions.map((session: LessonPlanSessionModel) => {
                                const label = session.topic || session.theme || `Session ${session.session_number ?? session.id}`;
                                const sessionAllSlidesTracked = areAllSlidesTrackedForSession(session);
                                return (
                                    <MenuItem key={session.id} value={session.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                            {sessionAllSlidesTracked && (
                                                <TaskAltIcon color="success" fontSize="small" titleAccess="All slides in this session tracked" />
                                            )}
                                            <Typography>{label}</Typography>
                                        </Box>
                                    </MenuItem>
                                );
                            })}
                        </TrackedTextField>
                        <ButtonGroup variant="outlined" aria-label="lesson plan navigation">
                            <Button onClick={handlePrevious} disabled={isFirstSession} startIcon={<ArrowBackIcon />} data-attr="lesson-plan-renderer_previous-session">
                                Previous
                            </Button>
                            <Button onClick={handleNext} disabled={isLastSession} endIcon={<ArrowForwardIcon />} data-attr="lesson-plan-renderer_next-session">
                                Next
                            </Button>
                        </ButtonGroup>
                    </Box>
                </Box>
            )}

            {sessions.map((session: LessonPlanSessionModel, index: number) => {
                const sessionNum = session.session_number ?? index + 1;
                const showSurveyCta = (sessionNum === 4 || sessionNum === 8 || sessionNum === 12) && lessonPlanId != null;
                return (
                    <Box key={session.id}>
                        <LessonPlanCard
                            session={session}
                            expanded={activeSessionId === session.id}
                            onChange={handleAccordionChange(session)}
                            cardRef={(el: HTMLDivElement | null) => { cardRefs.current[index] = el; }}
                            lessonPlanId={lessonPlanId}
                            myRoles={myRoles}
                            isPublished={!!entity.coach_delivered}
                        />
                        {showSurveyCta && (
                            <ProgressSurveyCta
                                lessonPlanId={lessonPlanId}
                                weekNum={sessionNum as 4 | 8 | 12}
                                sessions={sessions}
                                myRoles={myRoles}
                            />
                        )}
                    </Box>
                );
            })}

            <Dialog
                open={showChangeLessonDialog}
                onClose={handleDialogCancel}
                aria-labelledby="change-lesson-dialog-title"
                aria-describedby="change-lesson-dialog-description"
            >
                <DialogTitle id="change-lesson-dialog-title">Recording in Progress</DialogTitle>
                <DialogContent>
                    <DialogContentText id="change-lesson-dialog-description">
                        You are currently recording. What would you like to do with the current recording?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogCancel} color="inherit" data-attr="lesson-plan-renderer_change-lesson-dialog-cancel">Cancel</Button>
                    <Button onClick={handleDialogDiscard} color="error" data-attr="lesson-plan-renderer_change-lesson-dialog-discard">Discard Recording</Button>
                    <Button onClick={handleDialogStopAndUpload} color="primary" variant="contained" data-attr="lesson-plan-renderer_change-lesson-dialog-stop-upload">Stop & Upload Recording</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={blocker.state === 'blocked'}
                onClose={navLeaveBusy ? undefined : handleNavigationStay}
                disableEscapeKeyDown={navLeaveBusy}
                aria-labelledby="recording-nav-guard-title"
                aria-describedby="recording-nav-guard-description"
            >
                <DialogTitle id="recording-nav-guard-title">Recording in progress</DialogTitle>
                <DialogContent>
                    <DialogContentText id="recording-nav-guard-description">
                        You have a lesson recording in progress or uploads pending. You can stop and save to the server, stay on this page, or leave and lose any unsaved audio.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleNavigationStay} color="inherit" disabled={navLeaveBusy} data-attr="lesson-plan-renderer_nav-guard-stay">
                        Stay on page
                    </Button>
                    <Button onClick={handleNavigationLeaveAnyway} color="error" disabled={navLeaveBusy} data-attr="lesson-plan-renderer_nav-guard-leave">
                        Leave without saving
                    </Button>
                    <Button
                        onClick={handleNavigationStopAndUpload}
                        color="primary"
                        variant="contained"
                        disabled={navLeaveBusy}
                        data-attr="lesson-plan-renderer_nav-guard-stop-upload"
                    >
                        {navLeaveBusy ? 'Saving…' : 'Stop and upload'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LessonPlanRenderer;
